import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { calcClientDerivatives } from '@shared';
import {
  summarizeCareScenario,
  withCareScenarioChange,
  type CareScenarioChange,
} from '@shared/consumerPlan';
import { toast } from '@/components/hooks/use-toast';
import { fetchClientByClientIdRequest, previewClientUpdates, selectMaybeClientPartner } from '@/model';
import { consumerApi, consumerCarePlanSaveKey, consumerQuoteKeys } from '../api/consumer-api';

interface CarePlanSave {
  clientId: string;
  careEnvironmentSelections?: CareEnvironmentSelections;
  inferences?: Pick<CareScenarioChange, 'careHoursMultiplier' | 'familyCareShare'>;
}

function saveCarePlan({ clientId, careEnvironmentSelections, inferences }: CarePlanSave) {
  return Promise.all([
    careEnvironmentSelections && consumerApi.saveCareEnvironmentSelections(clientId, careEnvironmentSelections),
    inferences && consumerApi.saveCustomInferences(clientId, inferences),
  ]);
}

/**
 * Previews and saves changes to the consumer's care plan through the same endpoints the
 * edit-assumptions screen uses, so every other part of the app sees the same scenario.
 *
 * The server prices the quotes from the saved care plan while the cards project them on the
 * previewed one, so the quotes count as loading from the first save until they are re-priced.
 */
export function useCarePlanEditor(client: Client | null) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const clientPartner = useSelector(selectMaybeClientPartner);
  const clientId = client?.clientId ?? '';
  const mutationKey = useMemo(() => consumerCarePlanSaveKey(clientId), [clientId]);

  // Saves share one scope so they reach the server in order; only the last one re-prices the quotes.
  const { mutate: save } = useMutation({
    mutationKey,
    mutationFn: saveCarePlan,
    scope: { id: mutationKey.join('-') },
    // The preview was applied before saving, so a failed save reloads the client to drop it.
    onError: (_error, { clientId: failedClientId }) => {
      toast({
        variant: 'destructive',
        title: 'We couldn’t save your last change',
        description: 'Please check your connection and make that choice again.',
      });
      dispatch(fetchClientByClientIdRequest(failedClientId));
    },
    onSettled: (_saved, _error, variables) => {
      if (queryClient.isMutating({ mutationKey }) > 1) return;
      return queryClient.invalidateQueries({
        queryKey: consumerQuoteKeys.detail(variables.clientId),
        refetchType: 'all',
      });
    },
  });

  const derive = useCallback(
    (changed: Client) => calcClientDerivatives(changed, clientPartner) as Client,
    [clientPartner],
  );

  const previewScenario = useCallback(
    (change: CareScenarioChange) =>
      client ? summarizeCareScenario(derive(withCareScenarioChange(client, change))) : null,
    [client, derive],
  );

  const saveScenario = useCallback(
    (change: CareScenarioChange) => {
      if (!client) return;
      const changed = withCareScenarioChange(client, change);
      const { careHoursMultiplier, familyCareShare } = change;
      const changesInferences = careHoursMultiplier !== undefined || familyCareShare !== undefined;

      dispatch(previewClientUpdates(derive(changed)));
      save({
        clientId: client.clientId,
        careEnvironmentSelections: change.careSetting ? changed.careEnvironmentSelections : undefined,
        inferences: changesInferences ? { careHoursMultiplier, familyCareShare } : undefined,
      });
    },
    [client, derive, dispatch, save],
  );

  return { previewScenario, saveScenario };
}
